import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import { API_URL } from "@/config";
import { Users, TrendingUp, PhoneCall, ShoppingCart, Loader2, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface Lead {
    cust_leadid: number;
    cust_fname: string;
    cust_lname: string;
    cust_email: string;
    cust_phoneno: string | null;
    cust_lead_type: string | null;
    cust_lead_source: string | null;
    cust_status: string | null;
    cust_comment: string | null;
    cust_vehicle_id: number | null;
    cust_lead_created_at: string | null;
}

const STATUS_COLOR: Record<string, string> = {
    NEW:       "bg-blue-100 text-blue-700",
    CONTACTED: "bg-orange-100 text-orange-700",
    SCHEDULED: "bg-violet-100 text-violet-700",
    HOT:       "bg-red-100 text-red-700",
    WARM:      "bg-yellow-100 text-yellow-700",
    COLD:      "bg-gray-100 text-gray-500",
    CONVERTED: "bg-green-100 text-green-700",
    LOST:      "bg-gray-100 text-gray-400",
};

export default function DealersLeadsPage() {
    const { user }  = useAuth();
    const dealerId  = user?.dealerId;
    const [leads,   setLeads]   = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter,  setFilter]  = useState("ALL");
    const [search,  setSearch]  = useState("");

    useEffect(() => {
        if (!dealerId) return;
        http.get(`/leads/${dealerId}`)
            .then((r) => setLeads(r.data ?? []))
            .catch(() => setLeads([]))
            .finally(() => setLoading(false));
    }, [dealerId]);

    const active    = leads.filter((l) => !["CONVERTED", "LOST"].includes(l.cust_status ?? "NEW")).length;
    const hot       = leads.filter((l) => l.cust_status === "HOT").length;
    const converted = leads.filter((l) => l.cust_status === "CONVERTED").length;

    const visible = leads.filter((l) => {
        const matchStatus = filter === "ALL" || l.cust_status === filter || (!l.cust_status && filter === "NEW");
        const q = search.toLowerCase();
        const matchSearch = !q || `${l.cust_fname} ${l.cust_lname} ${l.cust_email} ${l.cust_phoneno ?? ""}`.toLowerCase().includes(q);
        return matchStatus && matchSearch;
    });

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">Leads</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">{leads.length} total leads</p>
                </div>
                <Link
                    to="/dealer/leads/buy"
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 transition"
                >
                    <ShoppingCart className="h-4 w-4" /> Buy Leads
                </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: "Active Leads",  value: active,    icon: Users,      color: "text-blue-500",  bg: "bg-blue-50"  },
                    { label: "Hot Leads",     value: hot,       icon: TrendingUp, color: "text-red-500",   bg: "bg-red-50"   },
                    { label: "Converted",     value: converted, icon: PhoneCall,  color: "text-green-500", bg: "bg-green-50" },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">{loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center">
                <div className="flex flex-wrap gap-1">
                    {["ALL", "HOT", "NEW", "WARM", "CONTACTED", "SCHEDULED", "COLD", "CONVERTED", "LOST"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`px-3 py-1 text-xs rounded-full border transition ${
                                filter === s ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 text-gray-500 hover:border-indigo-400"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <input
                    type="text"
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm min-w-48"
                />
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : visible.length === 0 ? (
                    <div className="py-10 text-center">
                        <p className="text-sm text-muted-foreground">No leads found.</p>
                        <Link to="/dealer/leads/buy" className="mt-2 inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
                            Buy leads to get started <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-400 border-b bg-gray-50">
                            <tr>
                                <th className="text-left px-5 py-3">Name</th>
                                <th className="text-left px-5 py-3">Contact</th>
                                <th className="text-left px-5 py-3">Type</th>
                                <th className="text-left px-5 py-3">Source</th>
                                <th className="text-left px-5 py-3">Status</th>
                                <th className="text-left px-5 py-3">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {visible.map((l) => (
                                <tr key={l.cust_leadid} className="hover:bg-gray-50">
                                    <td className="px-5 py-3 font-medium">{l.cust_fname} {l.cust_lname}</td>
                                    <td className="px-5 py-3">
                                        <p className="text-gray-500">{l.cust_email}</p>
                                        {l.cust_phoneno && <p className="text-gray-400 text-xs">{l.cust_phoneno}</p>}
                                    </td>
                                    <td className="px-5 py-3 text-gray-500">{l.cust_lead_type ?? "—"}</td>
                                    <td className="px-5 py-3 text-gray-500">{l.cust_lead_source ?? "—"}</td>
                                    <td className="px-5 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[l.cust_status ?? "NEW"] ?? "bg-gray-100 text-gray-600"}`}>
                                            {l.cust_status ?? "NEW"}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-gray-400">
                                        {l.cust_lead_created_at ? new Date(l.cust_lead_created_at).toLocaleDateString() : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
